import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
    useCallback,
    useRef,
} from 'react'
import { useSelector } from 'react-redux'
import { axiosInstance } from '../helpers/axios'
import {
    Conversation,
    MessageFeedback,
    ProjectPlanResponse,
    ChatMessageContent,
    AgentName,
    AIUser,
    CreateConversationRequest,
} from '../navigation/layouts/Lori/types'
import { useUser } from '../hooks/useUser'
import { useProjectPlan } from '../hooks/useProjectPlan'
import { APARTMENT } from '../helpers'
import { OrderedUnit } from '../models/Infrastructure'
import { RootState } from '../store'

type ButtonEventType =
    | 'build_turn_project'
    | 'lori_review'
    | 'retry_previous_message'
    | 'get_previous_turns'

type ButtonEvent = {
    type: ButtonEventType
    messageId: number
    createdAt: number
}

interface LoriState {
    // Original Lori state
    loading: boolean
    thinking: boolean
    connectionReady: boolean
    activeAgent: AgentName | null
    setLoading: (loading: boolean) => void
    setThinking: (thinking: boolean) => void
    setConnectionReady: (ready: boolean) => void

    // WebSocket state and methods
    socket: WebSocket | null
    userInput: string
    handleSendMessage: (
        message: string,
        activeURL: string,
        user?: AIUser,
        reformat?: boolean,
    ) => void
    setUserInput: (input: string) => void

    // Conversation state
    conversationHistory: Conversation[]
    projectPlanList: ProjectPlanResponse[]
    isRefreshing: boolean
    pendingWorkspaceChange: boolean
    activeConversation: Conversation | null
    conversationFeedback: MessageFeedback[]
    pinnedConversations: Conversation[]

    // Fresh message tracking
    freshMessageIds: Set<number>
    removeFromFreshMessageIds: (id: number) => void

    // Conversation methods
    getConversationHistory: (
        apartmentId: number,
    ) => Promise<Conversation[] | null>
    createConversation: (
        params: CreateConversationRequest,
    ) => Promise<Conversation | null>
    updateConversationState: (setActive?: boolean) => Promise<void>
    pinConversation: (conversationId: number) => Promise<void>
    getFeedback: (conversationId: number) => Promise<void>
    addFeedback: (
        feedback: boolean,
        context: string,
        chatMessageContentId: number,
    ) => Promise<void>
    setActiveConversation: (conversation: Conversation | null) => void
    setPendingWorkspaceChange: (pending: boolean) => void

    // Highlight Icon state
    highlightWTIcon: boolean
    setHighlightWTIcon: (highlight: boolean) => void
    highlightEZSchedulerIcon: boolean
    setHighlightEZSchedulerIcon: (highlight: boolean) => void
    scheduleFilterOpen: boolean
    setScheduleFilterOpen: (open: boolean) => void

    // Reload data state
    shouldRefresh: boolean
    setShouldRefresh: (refresh: boolean) => void

    // Button display state
    buttonEvent: ButtonEvent | null
    setButtonEvent: (buttonEvent: ButtonEvent | null) => void

    // Unread Lori messages (incremented when the Lori chat is not visible)
    unreadLoriMessages: number
    clearUnreadLoriMessages: () => void
}

export interface UnitsSelected {
    folder: string
    unitsSelected: number
}

const LoriContext = createContext<LoriState | undefined>(undefined)

export function LoriProvider({ children }: { children: ReactNode }) {
    // Original Lori state
    const [loading, setLoading] = useState(false)
    const [thinking, setThinking] = useState(false)
    const [connectionReady, setConnectionReady] = useState(false)
    const [activeAgent, setActiveAgent] = useState<AgentName | null>(null)

    // WebSocket state
    const [socket, setSocket] = useState<WebSocket | null>(null)
    const [userInput, setUserInput] = useState('')

    // Conversation state
    const [conversationHistory, setConversationHistory] = useState<
        Conversation[]
    >([])
    const [projectPlanList, setProjectPlanList] = useState<
        ProjectPlanResponse[]
    >([])
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [pendingWorkspaceChange, setPendingWorkspaceChange] = useState(false)
    const [
        activeConversation,
        setActiveConversation,
    ] = useState<Conversation | null>(null)
    const [conversationFeedback, setConversationFeedback] = useState<
        MessageFeedback[]
    >([])
    const [pinnedConversations, setPinnedConversations] = useState<
        Conversation[]
    >([])

    // Fresh message tracking
    const [freshMessageIds, setFreshMessageIds] = useState<Set<number>>(
        new Set(),
    )

    const removeFromFreshMessageIds = useCallback((id: number) => {
        setFreshMessageIds((prev) => {
            const newSet = new Set(prev)
            newSet.delete(id)
            return newSet
        })
    }, [])

    // Button display state

    const [buttonEvent, setButtonEvent] = useState<ButtonEvent | null>(null)

    // Highlight Icon state
    const [highlightWTIcon, setHighlightWTIcon] = useState(false)
    const [highlightEZSchedulerIcon, setHighlightEZSchedulerIcon] = useState(
        false,
    )
    const [scheduleFilterOpen, setScheduleFilterOpen] = useState(true)

    // Reload data state
    const [shouldRefresh, setShouldRefresh] = useState(false)

    // Unread Lori messages
    const [unreadLoriMessages, setUnreadLoriMessages] = useState(0)
    const selectedLoriChannel = useSelector(
        (state: RootState) => state.user.selectedLoriChannel,
    )
    // Ref mirror so the WS onmessage closure can read the latest channel
    // without rebuilding the socket whenever the channel toggles.
    const selectedLoriChannelRef = useRef(selectedLoriChannel)
    useEffect(() => {
        selectedLoriChannelRef.current = selectedLoriChannel
        if (selectedLoriChannel === 'lori') {
            setUnreadLoriMessages(0)
        }
    }, [selectedLoriChannel])

    const clearUnreadLoriMessages = useCallback(() => {
        setUnreadLoriMessages(0)
    }, [])

    const { workspaceUser } = useUser()
    const { getProjectPlanList } = useProjectPlan()
    const WEBSOCKET_BASE_URL = process.env.REACT_APP_WEBSOCKET_BASE_URL

    // Reconnect machinery. The WS effect below owns socket lifecycle; when a
    // non-clean close happens, onclose schedules a setState on reconnectTick
    // which forces the effect to re-fire with an exponential-backoff delay.
    const reconnectAttemptRef = useRef(0)
    const [reconnectTick, setReconnectTick] = useState(0)

    // WebSocket lifecycle: one socket per (conversationId, workspaceId).
    // This effect is the single source of truth for open/close; clearing
    // activeConversation (e.g. on workspace teardown below) causes the
    // cleanup to run and the socket to close cleanly.
    useEffect(() => {
        if (workspaceUser?.active_workspace.company_type !== APARTMENT) return
        if (!activeConversation?.id) return
        if (!workspaceUser?.active_workspace.id) return
        if (!WEBSOCKET_BASE_URL) return

        setConnectionReady(false)

        const url = `${WEBSOCKET_BASE_URL}chat/${activeConversation.id}/${workspaceUser.active_workspace.id}/`
        const ws = new WebSocket(url)

        const maxReconnectDelay = 32000
        const baseDelay = 10000

        ws.onopen = () => {
            reconnectAttemptRef.current = 0
        }

        const processMessage = (message: ChatMessageContent) => {
            // Pass the backend's agent_name through; ChatMessage decides how
            // to render based on both agent_name and function_call so cards
            // still work even when the backend tags them as 'system_event'.
            const resolvedAgentName =
                message.agent_name ??
                (message.role === 'system' ? 'system_event' : null)

            const newMessageObj = {
                id: message.id,
                role: message.role,
                content_type: message.content_type,
                function_call: message.function_call,
                function_call_response: message.function_call_response,
                message_items: message.message_items,
                agent_name: resolvedAgentName,
                timestamp: new Date().toISOString(),
            }
            if (message.active_agent) {
                setActiveAgent(message.active_agent)
            }

            if (message.id !== null && message.id !== undefined) {
                setFreshMessageIds((prev) =>
                    new Set(prev).add(message.id as number),
                )
            }

            setActiveConversation((prevConversation) => {
                if (!prevConversation) return null
                return {
                    ...prevConversation,
                    chat_messages: [
                        ...prevConversation.chat_messages,
                        newMessageObj,
                    ],
                }
            })

            const isUserVisible =
                message.content_type === 'text' ||
                message.content_type === 'card' ||
                message.content_type === 'button'
            if (
                isUserVisible &&
                selectedLoriChannelRef.current !== 'lori' &&
                message.role !== 'user'
            ) {
                setUnreadLoriMessages((c) => c + 1)
            }
        }

        const processConnectionStatus = (message: ChatMessageContent) => {
            if (
                message.content_type === 'connection_status' &&
                message.function_call === 'ready'
            ) {
                setConnectionReady(true)
            }
        }

        const processHighlight = (message: ChatMessageContent) => {
            if (message.content_type === 'highlight') {
                if (message.function_call === 'store_project_plan') {
                    setHighlightWTIcon(true)
                }
                if (message.function_call === 'schedule_project') {
                    setHighlightEZSchedulerIcon(true)
                    setScheduleFilterOpen(false)
                }
            }
        }

        const processReloadData = (message: ChatMessageContent) => {
            if (message.content_type === 'reload_data') {
                if (
                    message.function_call === 'store_deadline' ||
                    message.function_call === 'store_project' ||
                    message.function_call === 'create_workorder'
                ) {
                    setShouldRefresh(true)
                }
            }
        }

        const processButtonDisplay = (message: ChatMessageContent) => {
            if (message.content_type !== 'button') return
            if (message.id === null || message.id === undefined) return
            if (message.function_call === 'send_turn_summary') {
                setButtonEvent({
                    type: 'lori_review',
                    messageId: message.id,
                    createdAt: new Date().getTime(),
                })
            }
            if (message.function_call === 'retry_previous_message') {
                setButtonEvent({
                    type: 'retry_previous_message',
                    messageId: message.id,
                    createdAt: new Date().getTime(),
                })
            }
            if (message.function_call === 'get_previous_turns') {
                setButtonEvent({
                    type: 'get_previous_turns',
                    messageId: message.id,
                    createdAt: new Date().getTime(),
                })
            }
        }

        ws.onmessage = (e) => {
            const message: ChatMessageContent = JSON.parse(e.data)
            if (message.role === 'assistant') {
                setThinking(false)
            }
            processConnectionStatus(message)
            processHighlight(message)
            processMessage(message)
            processReloadData(message)
            processButtonDisplay(message)
        }

        let reconnectTimer: ReturnType<typeof setTimeout> | null = null
        ws.onclose = (e) => {
            if (e.code === 1000) return
            const delay = Math.min(
                Math.pow(2, reconnectAttemptRef.current) * baseDelay,
                maxReconnectDelay,
            )
            reconnectTimer = setTimeout(() => {
                reconnectAttemptRef.current += 1
                setReconnectTick((t) => t + 1)
            }, delay)
        }

        ws.onerror = () => {}

        setSocket(ws)

        return () => {
            if (reconnectTimer) {
                clearTimeout(reconnectTimer)
            }
            // Detach handlers before close so a pending reconnect timer from
            // a previous-generation socket can't leak state into the next one.
            ws.onopen = null
            ws.onmessage = null
            ws.onclose = null
            ws.onerror = null
            if (
                ws.readyState === WebSocket.OPEN ||
                ws.readyState === WebSocket.CONNECTING
            ) {
                ws.close(1000)
            }
            setSocket(null)
        }
    }, [
        activeConversation?.id,
        workspaceUser?.active_workspace.id,
        workspaceUser?.active_workspace.company_type,
        WEBSOCKET_BASE_URL,
        reconnectTick,
    ])

    // Workspace teardown: when the user switches workspaces, reset any
    // workspace-scoped Lori state so the WS effect doesn't open a socket on
    // a stale (old_conversation, new_workspace) tuple and the UI doesn't show
    // the previous workspace's conversation.
    const prevWorkspaceIdRef = useRef<number | undefined>(
        workspaceUser?.active_workspace.id,
    )
    useEffect(() => {
        const currentId = workspaceUser?.active_workspace.id
        if (
            prevWorkspaceIdRef.current !== undefined &&
            prevWorkspaceIdRef.current !== currentId
        ) {
            setActiveConversation(null)
            setConversationHistory([])
            setPinnedConversations([])
            setConversationFeedback([])
            setFreshMessageIds(new Set())
            setThinking(false)
            setConnectionReady(false)
            setButtonEvent(null)
            setUnreadLoriMessages(0)
            reconnectAttemptRef.current = 0
        }
        prevWorkspaceIdRef.current = currentId
    }, [workspaceUser?.active_workspace.id])

    // Take the unit selector message in format
    function reformUnitsMessage(message: {
        orderedUnits: OrderedUnit[]
    }): UnitsSelected[] {
        const folderCounts: Record<string, number> = {}

        for (const unit of message.orderedUnits) {
            folderCounts[unit.folderName] =
                (folderCounts[unit.folderName] || 0) + 1
        }

        return Object.entries(folderCounts).map(([folder, unitsSelected]) => ({
            folder,
            unitsSelected,
        }))
    }

    // Message handling
    const handleSendMessage = (
        message: string,
        activeURL: string,
        user: AIUser = 'user',
        reformat: boolean = false,
    ) => {
        if (message.trim()) {
            socket?.send(
                JSON.stringify({
                    message: message,
                    user: user,
                    activeURL: activeURL || null,
                }),
            )

            const userMessage = user === 'user'
            if (userMessage && activeConversation) {
                setActiveConversation((prevConversation) => {
                    if (!prevConversation) return null
                    // reformat the units message
                    let reformattedMessage = message
                    if (reformat) {
                        const unitsArr = reformUnitsMessage(JSON.parse(message))
                        reformattedMessage = unitsArr
                            .map(
                                (u) =>
                                    `Folder ${u.folder}: ${u.unitsSelected} units selected`,
                            )
                            .join('\n')
                    }
                    return {
                        ...prevConversation,
                        chat_messages: [
                            ...prevConversation.chat_messages,
                            {
                                id: null,
                                role: 'user',
                                content_type: 'text',
                                function_call: null,
                                message_items: [
                                    {
                                        key: 'message',
                                        value: reformattedMessage,
                                    },
                                ],
                                active_agent: null,
                            },
                        ],
                    }
                })
                setUserInput('')
                setThinking(true)
            }
        }
    }

    // Load initial conversation data
    useEffect(() => {
        if (!workspaceUser?.active_workspace.id) {
            return
        }
        const currentWorkspaceId = workspaceUser.active_workspace.id
        getConversationHistory(currentWorkspaceId)
        getProjectPlanList(true)
    }, [workspaceUser?.active_workspace.id])

    // Update conversation state when history changes. No !activeConversation
    // guard: workspace teardown always nulls activeConversation, so this path
    // must run on the new workspace's fresh history even when we previously
    // had one selected. updateConversationState only flips to setActive=true,
    // and will reuse the existing activeConversation if it matches.
    useEffect(() => {
        if (conversationHistory.length > 0) {
            updateConversationState(true)
        }
    }, [conversationHistory])

    const getConversationHistory = async (
        apartmentId: number,
    ): Promise<Conversation[] | null> => {
        try {
            const activeRes = await axiosInstance.post<Conversation>(
                '/chat/conversation/get_or_create_web_conversation/',
                { agent: 'triage' },
            )
            const activeConversation = activeRes.data
            setActiveConversation(activeConversation)
            getFeedback(activeConversation.id)

            const res = await axiosInstance.get<Conversation[]>(
                '/chat/conversation/',
                {
                    params: { apartment_id: apartmentId },
                },
            )

            const history = res.data ?? []
            setConversationHistory(history)
            setPinnedConversations(
                history.filter(
                    (conversation: Conversation) => conversation.pinned,
                ),
            )
            return history
        } catch (err) {
            console.error('Failed to fetch conversation history', err)
            return null
        }
    }

    const createConversation = async (
        params: CreateConversationRequest = { agent: 'triage' },
    ) => {
        setActiveConversation(null)
        try {
            const res = await axiosInstance.post<Conversation>(
                '/chat/conversation/',
                params,
            )
            setActiveConversation(res.data)
            setConversationHistory((prev) => [res.data, ...prev])
            setThinking(false)
            setConnectionReady(false)
            return res.data
        } catch (err) {
            console.error('Failed to create conversation', err)
            return null
        }
    }

    const updateConversationState = async (setActive: boolean = false) => {
        if (conversationHistory.length === 0) return
        // Set pinned conversations
        const pinned = conversationHistory.filter(
            (conversation: Conversation) => conversation.pinned,
        )
        setPinnedConversations(pinned)

        if (setActive) {
            const webConversations = conversationHistory.filter(
                (c) => c.conversation_type === 'APARTMENT_WEB',
            )

            if (webConversations.length > 0) {
                const mostRecentConversation = webConversations.reduce(
                    (latest: Conversation, current: Conversation) => {
                        return new Date(current.created_date) >
                            new Date(latest.created_date)
                            ? current
                            : latest
                    },
                    webConversations[0],
                )
                setActiveConversation(mostRecentConversation)
                getFeedback(mostRecentConversation.id)
            } else {
                const created = await createConversation({ agent: 'triage' })
                if (created) {
                    getFeedback(created.id)
                }
            }
        }
    }

    const pinConversation = async (conversationId: number) => {
        try {
            const res = await axiosInstance.post(
                `/chat/conversation/${conversationId}/pin/`,
            )
            setPinnedConversations([...pinnedConversations, res.data])
        } catch (err) {
            console.error(err)
        }
    }

    const getFeedback = async (conversationId: number) => {
        try {
            const res = await axiosInstance.get(
                `/chat/conversation/${conversationId}/get_feedback/`,
            )
            setConversationFeedback(res.data)
        } catch (err) {
            console.error(err)
        }
    }

    const addFeedback = async (
        feedback: boolean,
        context: string,
        chatMessageContentId: number,
    ) => {
        if (!activeConversation) return
        try {
            await axiosInstance.post(
                `/chat/conversation/${activeConversation.id}/add_feedback/`,
                {
                    feedback,
                    context,
                    chat_message_content_id: chatMessageContentId,
                },
            )
            setConversationFeedback((prev) => {
                const existing = prev.findIndex(
                    (f) => f.message_id === chatMessageContentId,
                )
                if (existing >= 0) {
                    return prev.map((f) =>
                        f.message_id === chatMessageContentId
                            ? { ...f, feedback }
                            : f,
                    )
                }
                return [...prev, { message_id: chatMessageContentId, feedback }]
            })
        } catch (err) {
            console.error(err)
        }
    }

    const value = {
        // Original Lori state
        loading,
        thinking,
        connectionReady,
        activeAgent,
        setLoading,
        setThinking,
        setConnectionReady,

        // WebSocket state and methods
        socket,
        userInput,
        handleSendMessage,
        setUserInput,

        // Conversation state
        conversationHistory,
        projectPlanList,
        isRefreshing,
        pendingWorkspaceChange,
        activeConversation,
        conversationFeedback,
        pinnedConversations,

        // Fresh message tracking
        freshMessageIds,
        removeFromFreshMessageIds,

        // Conversation methods
        getConversationHistory,
        createConversation,
        updateConversationState,
        pinConversation,
        getFeedback,
        addFeedback,
        setActiveConversation,
        setPendingWorkspaceChange,

        // Highlight Icon state
        highlightWTIcon,
        setHighlightWTIcon,
        highlightEZSchedulerIcon,
        setHighlightEZSchedulerIcon,
        scheduleFilterOpen,
        setScheduleFilterOpen,

        // Reload data state
        shouldRefresh,
        setShouldRefresh,

        // Button display state
        buttonEvent,
        setButtonEvent,

        // Unread Lori messages
        unreadLoriMessages,
        clearUnreadLoriMessages,
    }

    return <LoriContext.Provider value={value}>{children}</LoriContext.Provider>
}

export const useLoriState = (): LoriState => {
    const context = useContext(LoriContext)
    if (context === undefined) {
        throw new Error('useLoriState must be used within a LoriProvider')
    }
    return context
}

export type LoriStateController = LoriState
